import type { IBaseNotary } from "../BaseNotaryFactory";
import type User from "le-coffre-resources/dist/Notary";
import type { QueryPayload } from "../../../types";
import type { IGetUsersParams } from "./Users";
import { buildUsersUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

export type UsersReadSlice = {
	get(q?: IGetUsersParams): Promise<User[]>;
	getAggregated(): Promise<User[]>;
	getByUid(uid: string, q?: QueryPayload): Promise<User>;
	getMyProfileEmails(): Promise<MyProfileEmailsApiResponse>;
};

export type MyProfileEmailsApiResponse = {
	contactEmail: string | null;
	idnotEmail: string | null;
	shareInviteEmail: string | null;
};

export type IPutNotaryUsersParams = {
	uid?: User["uid"];
	office_role?: User["office_role"];
	role?: User["role"];
};

export type UsersWriteSlice = {
	put(uid: string, body: IPutNotaryUsersParams): Promise<User>;
	putMyContactEmail(email: string): Promise<void>;
};

async function usersGetRequest<T>(baseNotary: IBaseNotary, url: URL): Promise<T> {
	try {
		return await baseNotary.getRequest<T>(url);
	} catch (err) {
		baseNotary.onError(err);
		return Promise.reject(err);
	}
}

export function buildUsersReadOperations(baseNotary: IBaseNotary, baseUrl: string): UsersReadSlice {
	return {
		get: async (q?: IGetUsersParams): Promise<User[]> => {
			const url = new URL(baseUrl);
			if (q) baseNotary.appendJsonSearchParams(url, { q });
			return usersGetRequest<User[]>(baseNotary, url);
		},
		getAggregated: async (): Promise<User[]> => usersGetRequest<User[]>(baseNotary, new URL(baseNotary.namespaceUrl.concat("/users/aggregated"))),
		getByUid: async (uid: string, q?: QueryPayload): Promise<User> => {
			const url = new URL(baseUrl.concat("/").concat(uid));
			if (q) baseNotary.appendJsonSearchParams(url, q);
			return usersGetRequest<User>(baseNotary, url);
		},
		getMyProfileEmails: async (): Promise<MyProfileEmailsApiResponse> =>
			usersGetRequest<MyProfileEmailsApiResponse>(baseNotary, new URL(baseNotary.namespaceUrl.concat("/users/me/profile-emails"))),
	};
}

export function buildUsersWriteOperations(baseNotary: IBaseNotary, baseUrl: string): UsersWriteSlice {
	return {
		put: async (uid: string, body: IPutNotaryUsersParams): Promise<User> => {
			const url = new URL(baseUrl.concat("/").concat(uid));
			try {
				const payload = buildUsersUpdatePayload(body);
				return await baseNotary.putRequest<User>(url, payload);
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
		putMyContactEmail: async (email: string): Promise<void> => {
			const url = new URL(baseNotary.namespaceUrl.concat("/users/me/contact-email"));
			try {
				await baseNotary.putRequest<{ ok?: boolean }>(url, { email });
			} catch (err) {
				baseNotary.onError(err);
				return Promise.reject(err);
			}
		},
	};
}
